import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MAX_BYTES = 800 * 1024;

export default function ImageInput({
    value,
    onChange,
    label = "Imagen",
    aspect = "aspect-square",
    testid = "image-input",
}) {
    const fileRef = useRef(null);
    const [mode, setMode] = useState("url"); // url | upload
    const [loading, setLoading] = useState(false);

    const handleFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("El archivo debe ser una imagen.");
            return;
        }
        if (file.size > MAX_BYTES * 4) {
            toast.error("Imagen demasiado grande (máximo 3 MB).");
            return;
        }
        setLoading(true);
        // Compress via canvas to ≤ MAX_BYTES
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (e) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const maxSide = 1200;
                let { width, height } = img;
                if (width > maxSide || height > maxSide) {
                    if (width > height) {
                        height = Math.round((maxSide / width) * height);
                        width = maxSide;
                    } else {
                        width = Math.round((maxSide / height) * width);
                        height = maxSide;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                let q = 0.9;
                let dataUrl = canvas.toDataURL("image/jpeg", q);
                while (dataUrl.length > MAX_BYTES * 1.4 && q > 0.4) {
                    q -= 0.1;
                    dataUrl = canvas.toDataURL("image/jpeg", q);
                }
                onChange(dataUrl);
                setLoading(false);
            };
            img.onerror = () => {
                toast.error("No se pudo procesar la imagen.");
                setLoading(false);
            };
            img.src = e.target.result;
        };
        reader.onerror = () => {
            toast.error("Error leyendo el archivo.");
            setLoading(false);
        };
        reader.readAsDataURL(file);
    };

    return (
        <div data-testid={testid}>
            <p className="text-xs font-medium text-zinc-700 mb-2">{label}</p>
            <div className="flex gap-1 mb-3 p-1 rounded-lg border border-zinc-200 bg-white inline-flex">
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    data-testid={`${testid}-mode-url`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                        mode === "url"
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-600 hover:text-zinc-900"
                    }`}
                >
                    <LinkIcon className="w-3 h-3 mr-1 inline" />
                    URL
                </button>
                <button
                    type="button"
                    onClick={() => setMode("upload")}
                    data-testid={`${testid}-mode-upload`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                        mode === "upload"
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-600 hover:text-zinc-900"
                    }`}
                >
                    <Upload className="w-3 h-3 mr-1 inline" />
                    Subir archivo
                </button>
            </div>

            <div className="flex gap-3">
                <div
                    className={`${aspect} w-24 rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 grid place-items-center shrink-0`}
                >
                    {value ? (
                        <img
                            src={value}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : loading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                    ) : (
                        <span className="text-xs text-zinc-400">Vista previa</span>
                    )}
                </div>

                <div className="flex-1 space-y-2">
                    {mode === "url" ? (
                        <Input
                            type="url"
                            value={value && value.startsWith("data:") ? "" : value || ""}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="https://images.unsplash.com/..."
                            data-testid={`${testid}-url`}
                            className="h-10 text-sm"
                        />
                    ) : (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                hidden
                                data-testid={`${testid}-file`}
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileRef.current?.click()}
                                className="border-zinc-200 h-10"
                                disabled={loading}
                            >
                                <Upload className="w-4 h-4 mr-1.5" />
                                {loading ? "Procesando…" : "Seleccionar imagen"}
                            </Button>
                            <p className="text-xs text-zinc-500">
                                JPG/PNG · se ajusta automáticamente a ≤ 800 KB.
                            </p>
                        </>
                    )}
                    {value && (
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            data-testid={`${testid}-clear`}
                            className="text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
                        >
                            <X className="w-3 h-3" /> Quitar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
